<script setup lang="ts">
import { onMounted, type Ref } from 'vue';
import Column from 'primevue/column';
import { useRoute, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import { useToast } from 'primevue/usetoast';
import { errToToast } from '@/helpers/tools';
import { useBrowserSessionValue } from '@/helpers/browser_session';
import { useCollection, type SortOption } from '@/composables/managedCollection';
import KpBaseCollectionGrid from '@/components/KpBaseCollectionGrid.vue';
import { CO2Emissionsfaktor, CO2EmissionsfaktorCollection } from '@/models/CO2Emissionsfaktor';

const router = useRouter();
const route = useRoute();
const toast = useToast();

const tableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-tableSort`, [
    { direction: 'asc', property: 'energie_waermequelle' },
]);
const expandedRowGroups: Ref<string[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-expandedRowGroups`, []);
const pageSize: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-pageSize`, 100);
const page: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-page`, 1);
const {
    collection,
    serverTotal,
    reload: reloadCollection,
} = useCollection(new CO2EmissionsfaktorCollection(), {
    autoload: false,
    tableSort,
    pageSize,
    page,
});

onMounted(async () => {
    if (collection.loading.value !== true) {
        await collection.query();
    }
});

function editRecord(r: CO2Emissionsfaktor) {
    router.push({ name: 'bg_jw_co2_emissionsfaktor_edit', params: { id: r?.id } });
}

async function deleteRecord(r: CO2Emissionsfaktor) {
    if (window.confirm(getI18n().t('sicher_loeschen'))) {
        try {
            await r.destroy();
            await reloadCollection();
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

function newRecord() {
    router.push({ name: 'bg_jw_co2_emissionsfaktor_edit', params: { id: 'new' } });
}
</script>
<template>
    <div style="height: 100%">
        <KpBaseCollectionGrid
            :collection="collection"
            v-model:table-sort="tableSort"
            v-model:expandedRowGroups="expandedRowGroups"
            :title="$t('co2_emissionsfaktoren')"
            scrollable
            scroll-height="flex"
            paginator
            v-model:page="page"
            v-model:pageSize="pageSize"
            :total-records="serverTotal"
            @editrecord="editRecord($event as CO2Emissionsfaktor)"
            @deleterecord="deleteRecord($event as CO2Emissionsfaktor)"
            @new="newRecord"
        >
            <Column field="id" sortable :header="$t('id')" style="width: 5rem"></Column>
            <Column
                field="energie_waermequelle_id"
                sortable
                :header="$t('energie_waermequelle')"
                sort-field="energie_waermequelle"
                ><template #body="slotProps">{{ slotProps.data.energie_waermequelle }}</template></Column
            >
            <Column field="jahr" :header="$t('jahr')" filter-field="jahr" :showFilterMenu="false" sortable>
                <template #body="slotProps">
                    <div>{{ slotProps.data.jahr }}</div>
                </template>
            </Column>
            <Column field="faktor" sortable :header="$t('faktor_t_pro_mwh')">
                <template #body="{ data }">{{ data.faktor != null ? data.faktor * 1000 : '' }}</template>
            </Column>
        </KpBaseCollectionGrid>
    </div>
</template>
